import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { getLocation } from "@/content/locations";
import { MarkdownBody, prepareMarkdownBodyForPage } from "@/components/MarkdownBody";
import { ContactForm } from "@/components/ContactForm";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedNews } from "@/components/sections/RelatedNews";
import type { LocationTopic } from "@/content/types";
import { breadcrumbSchema, metaDescription, pageMetaTitle, SITE_URL } from "@/lib/seo";

const TOPICS: LocationTopic[] = [
  "pricing",
  "consultant-vs-agency",
  "trades",
  "professional-services",
  "ai-search",
];

function isTopic(s: string): s is LocationTopic {
  return (TOPICS as string[]).includes(s);
}

function stripFormNoise(body: string): string {
  const markers = [
    "## Get in touch",
    "Select a serviceSEO",
    "We use cookies to improve your experience",
  ];
  let cleaned = body;
  for (const m of markers) {
    const idx = cleaned.indexOf(m);
    if (idx !== -1) cleaned = cleaned.slice(0, idx).trimEnd();
  }
  return cleaned;
}

function stripFirstHeading(body: string): string {
  return body.replace(/^#\s+.+\n+/, "");
}

export const Route = createFileRoute("/seo/$location/$topic")({
  loader: ({ params }) => {
    const loc = getLocation(params.location);
    if (!loc) throw notFound();
    if (!isTopic(params.topic)) throw notFound();
    const page = loc.topics[params.topic];
    if (!page) throw notFound();
    return { hub: loc.hub, page };
  },
  head: ({ loaderData, params }) => {
    const url = `https://www.phil-carr.co.uk/seo/${params.location}/${params.topic}`;
    const description = loaderData ? metaDescription(loaderData.page.description) : "";
    return {
      meta: loaderData
        ? [
            { title: pageMetaTitle(`/seo/${params.location}/${params.topic}`, loaderData.page.title) },
            { name: "description", content: description },
            { property: "og:title", content: loaderData.page.title },
            { property: "og:description", content: description },
            { property: "og:url", content: url },
          ]
        : [],
      links: loaderData ? [{ rel: "canonical", href: url }] : [],
      scripts: loaderData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(
                breadcrumbSchema([
                  { name: "Home", url: `${SITE_URL}/` },
                  { name: "SEO", url: `${SITE_URL}/seo` },
                  {
                    name: loaderData.hub.title.replace(/\s*[—|].*$/, ""),
                    url: `${SITE_URL}/seo/${params.location}`,
                  },
                  { name: loaderData.page.title, url },
                ]),
              ),
            },
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-4xl">Page not found</h1>
      <p className="mt-4 text-muted-foreground">That topic isn't available for this city yet.</p>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: TopicPage,
});

function TopicPage() {
  const { hub, page } = Route.useLoaderData();
  const { location } = Route.useParams();
  const pageTitle = page.body.match(/^#\s+(.+)$/m)?.[1]?.trim() ?? page.title;
  const preparedBody = prepareMarkdownBodyForPage(stripFirstHeading(stripFormNoise(page.body)));
  return (
    <>
      <PageHero title={pageTitle} backgroundImage={preparedBody.leadImage?.src} />
      <article className="inner-content-section container mx-auto max-w-5xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
        <Link
          to="/seo/$location"
          params={{ location }}
          className="demo-back-link demo-link-arrow-button"
        >
          ← Back to {hub.title.replace(/\s*[—|].*$/, "")}
        </Link>
        <div className="mt-8">
          <MarkdownBody>{preparedBody.body}</MarkdownBody>
        </div>
        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/contact" className="demo-link-arrow-button group">
            Book a consultation
            <span className="demo-arrow-circle">
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </span>
          </Link>
        </div>
      </article>
      <RelatedNews
        slugs={[
          "seo-in-2026-how-local-yorkshire-businesses-can-win-more-customers-from-google",
          "common-seo-mistakes-that-hold-small-businesses-back",
          "local-seo-and-the-consumer-journey",
        ]}
      />
      <CtaBand />
      <section className="inner-contact-section border-t border-border bg-background">
        <div className="container mx-auto max-w-3xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <p className="demo-section-label">Get in touch</p>
          <h2 className="site-heading-lg mt-4 font-display font-light">
            Start a <em>conversation</em>
          </h2>
          <div className="mt-10">
            <ContactForm pageContext={page.title} />
          </div>
        </div>
      </section>
    </>
  );
}
